import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/** Número/dado — mesma DM Mono do Ranking LTV (KPI e tabelas). */
export const NUM_CLASS = 'font-mono font-semibold tabular-nums tracking-tight';
export const MONEY_CLASS = 'font-mono font-medium tabular-nums tracking-tight';

const axisTick = (fontFamily: string) => ({
    fill: 'var(--color-text-muted)',
    fontSize: 11,
    fontWeight: 500,
    fontFamily,
} as const);

export const chartTick = axisTick('var(--font-mono)');
export const chartTickSans = axisTick('var(--font-sans)');
export const CHART_MARGIN = { top: 8, right: 20, left: 4, bottom: 4 } as const;

const SEMANTIC_COLORS = [
    'var(--color-primary)',
    'var(--color-success)',
    'var(--color-warning)',
    'var(--color-danger)',
] as const;

export const chartSemanticColor = (index: number) =>
    SEMANTIC_COLORS[index % SEMANTIC_COLORS.length];

type TooltipItem = {
    name?: string;
    value?: number | string;
    color?: string;
    payload?: { name?: string };
};

/** Série (ex. Vendas) — não confundir com payload.name (categoria/eixo X). */
const itemLabel = (item: TooltipItem) => item.name ?? item.payload?.name;

export function ChartTooltip({
    active,
    payload,
    label,
    formatValue,
}: {
    active?: boolean;
    payload?: TooltipItem[];
    label?: string | number;
    formatValue?: (value: number | string | undefined, name?: string) => React.ReactNode;
}) {
    if (!active || !payload?.length) return null;

    const heading = label != null && label !== '' ? String(label) : null;
    const showHeading = heading != null && (
        payload.length > 1 || !payload.some((item) => itemLabel(item) === heading)
    );

    return (
        <div
            role="tooltip"
            className="rounded-md bg-gray-900 px-2.5 py-[5px] font-sans text-[11.5px] leading-snug text-white shadow-sm"
        >
            {showHeading ? (
                <p className="font-mono tabular-nums text-white/70">{heading}</p>
            ) : null}
            <ul className={showHeading ? 'mt-0.5 space-y-0.5' : 'space-y-0.5'}>
                {payload.map((item, i) => {
                    const name = itemLabel(item);
                    return (
                        <li key={i} className="flex items-center gap-1.5">
                            {item.color ? (
                                <span className="size-1.5 shrink-0 rounded-full" style={{ background: item.color }} />
                            ) : null}
                            {name ? <span className="text-white/80">{name}</span> : null}
                            <span className="font-mono font-medium tabular-nums">
                                {formatValue ? formatValue(item.value, name) : item.value}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export const formatChartCurrency = (value: number | string | undefined) => {
    const amount = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(amount)) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
};

interface DashboardCardProps {
    title: string;
    children: React.ReactNode;
    className?: string;
    /** Tabela encostada nas bordas — sem padding no corpo. */
    flush?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, children, className = '', flush = false }) => {
    return (
        <section className={cn('card flex h-full min-w-0 flex-col overflow-hidden', className)}>
            <header className={cn(
                'shrink-0 px-s4 pt-s4 md:px-s5 md:pt-s5',
                flush ? 'border-b border-subtle pb-s3' : '',
            )}>
                <h2 className="card-title mb-0">{title}</h2>
            </header>
            <div className={flush ? 'min-w-0 flex-1' : 'flex-1 px-s4 pb-s4 pt-s3 md:px-s5 md:pb-s5'}>
                {children}
            </div>
        </section>
    );
};

export const ChartEmpty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex h-52 flex-col items-center justify-center px-s4 text-center md:h-56">
        <p className="text-sm font-medium text-default">{children}</p>
    </div>
);

export const ChartBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = '',
}) => (
    <div className={cn('flex min-h-52 w-full flex-1 flex-col md:min-h-56', className)}>{children}</div>
);

/** Mede o slot flex e passa width/height numéricos — evita warning -1 do Recharts. */
export const ChartPlot: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState<{ width: number; height: number } | null>(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const measure = () => {
            const { width, height } = node.getBoundingClientRect();
            if (width > 0 && height > 0) {
                setSize((prev) =>
                    prev?.width === width && prev?.height === height ? prev : { width, height },
                );
                return;
            }
            // Container oculto / sem layout — desmonta o chart (senão o Recharts loga width(-1)).
            setSize(null);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className="relative min-h-52 w-full min-w-0 flex-1 md:min-h-56">
            {size ? React.cloneElement(children, { width: size.width, height: size.height }) : null}
        </div>
    );
};

export default DashboardCard;
